<script lang="ts">
    import { onMount, tick } from 'svelte';
    import { auth } from '../stores/auth';
    import { t } from '../stores/locale';
    import { push } from 'svelte-spa-router';

    // ── Protection ──────────────────────────────────────────────────────────
    // Redirect if not tech/admin
    $: if (!$auth.level || ($auth.level !== 'technician' && $auth.level !== 'admin')) {
        push('/');
    }

    type Line = { type: 'cmd' | 'out' | 'err'; text: string };

    let lines: Line[] = [
        { type: 'out', text: 'LISTmod-HU System CLI [Version 0.0.1-DEV]' },
        { type: 'out', text: 'Type "help" for a list of available commands.' },
        { type: 'out', text: '' }
    ];

    let currentInput = '';
    let history: string[] = [];
    let historyIndex = -1;
    let terminalEl: HTMLElement;
    let inputEl: HTMLInputElement;

    const commands: Record<string, (args: string[]) => string | void> = {
        help: () => {
            return 'Available commands:\n' +
                   '  help      - Show this help message\n' +
                   '  status    - Show system status summary\n' +
                   '  logs      - Show recent system logs\n' +
                   '  clear     - Clear terminal screen\n' +
                   '  whoami    - Show current user level\n' +
                   '  reboot    - Reboot the system (mock)\n' +
                   '  exit      - Exit terminal';
        },
        status: () => {
            return 'System: ONLINE\nUptime: 14d 2h 33m\nCPU: 12%\nRAM: 442MB / 1024MB';
        },
        logs: () => {
            return '[2026-03-22 21:45:01] INFO: System check passed\n' +
                   '[2026-03-22 21:50:12] WARN: High CPU temperature detected\n' +
                   '[2026-03-22 22:00:00] INFO: Periodic report generated';
        },
        clear: () => {
            lines = [];
        },
        whoami: () => {
            return `Current user level: ${$auth.level}`;
        },
        reboot: () => {
            return 'System reboot initiated... (Connection closed)';
        },
        exit: () => {
            push('/');
        }
    };

    async function handleSubmit() {
        const cmd = currentInput.trim();
        if (!cmd) return;

        lines = [...lines, { type: 'cmd', text: cmd }];
        history = [cmd, ...history.filter(h => h !== cmd)].slice(0, 50);
        historyIndex = -1;

        const [baseCmd, ...args] = cmd.toLowerCase().split(' ');
        
        await tick();
        scrollToBottom();

        setTimeout(async () => {
            if (commands[baseCmd]) {
                const output = commands[baseCmd](args);
                if (output) {
                    lines = [...lines, { type: 'out', text: output }];
                }
            } else {
                lines = [...lines, { type: 'err', text: `Command not found: ${baseCmd}` }];
            }
            currentInput = '';
            await tick();
            scrollToBottom();
        }, 50);
    }

    function handleKeyDown(e: KeyboardEvent) {
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (historyIndex < history.length - 1) {
                historyIndex++;
                currentInput = history[historyIndex];
            }
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (historyIndex > 0) {
                historyIndex--;
                currentInput = history[historyIndex];
            } else if (historyIndex === 0) {
                historyIndex = -1;
                currentInput = '';
            }
        }
    }

    function scrollToBottom() {
        if (terminalEl) {
            terminalEl.scrollTop = terminalEl.scrollHeight;
        }
    }

    function focusInput() {
        if (inputEl) inputEl.focus();
    }

    onMount(() => {
        focusInput();
    });
</script>

<div class="page">
    <h1>{$t('nav.terminal')}</h1>

    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="terminal-container" on:click={focusInput} bind:this={terminalEl}>
        <div class="terminal-content">
            {#each lines as line}
                <div class="line" class:line--cmd={line.type === 'cmd'} class:line--err={line.type === 'err'}>{#if line.type === 'cmd'}<span class="prompt">admin@listmod:~$ </span>{/if}<span class="text">{line.text}</span></div>
            {/each}
            
            <div class="input-line">
                <span class="prompt">admin@listmod:~$ </span>
                <!-- svelte-ignore a11y-autofocus -->
                <input
                    type="text"
                    bind:this={inputEl}
                    bind:value={currentInput}
                    on:keydown={handleKeyDown}
                    on:keypress={(e) => e.key === 'Enter' && handleSubmit()}
                    spellcheck="false"
                    autocomplete="off"
                    autofocus
                />
            </div>
        </div>
    </div>
</div>

<style lang="scss">
    .terminal-container {
        background-color: #0c0c0c;
        color: #cccccc;
        font-family: $font-family-mono;
        font-size: 0.9rem;
        border-radius: 8px;
        padding: 1.25rem;
        height: 60vh;
        overflow-y: auto;
        cursor: text;
        border: 1px solid #333;

        &::-webkit-scrollbar {
            width: 8px;
        }
        &::-webkit-scrollbar-track {
            background: #1a1a1a;
        }
        &::-webkit-scrollbar-thumb {
            background: #333;
            border-radius: 4px;
        }
    }

    .terminal-content {
        display: flex;
        flex-direction: column;
        gap: 0.2rem;
    }

    .line {
        white-space: pre-wrap;
        word-break: break-all;
        line-height: 1.5;

        &--cmd {
            color: #ffffff;
            font-weight: bold;
        }

        &--err {
            color: $color-danger;
        }
    }

    .input-line {
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }

    .prompt {
        color: #4CAF50;
        font-weight: bold;
        white-space: nowrap;
        user-select: none;
    }

    input {
        flex: 1;
        background: transparent !important;
        border: none !important;
        color: #ffffff !important; // Force white text for terminal input
        caret-color: currentColor;
        font-family: $font-family-mono;
        padding: 0 0.25em !important;
        font-size: inherit;
        margin: 0;
        outline: none !important;
        width: 100%;

        &:hover:not(:disabled),
        &:focus:not(:disabled) {
            background: transparent !important;
            border: none !important;
            outline: none !important;
            box-shadow: none !important;
        }
    }

    .text {
        flex: 1;
    }
</style>
